/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Grid, Box, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Switch, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tooltip, IconButton,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  CheckCircle, Cancel, HourglassEmpty, ReportProblem,
  Visibility as EyeIcon, PictureAsPdf as PdfIcon,
  CloudUpload as CloudUploadIcon, Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Chart, BarElement, ArcElement, CategoryScale, LinearScale } from 'chart.js';

import api from '../api';
import { getRoleFromToken } from '../utils/auth';

/* ─── Chart.js ──────────────────────────────────────────────── */
Chart.register(BarElement, ArcElement, CategoryScale, LinearScale);
const chartToJpeg = (type, data, options = {}, width = 600, height = 300) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  // eslint-disable-next-line no-new
  new Chart(canvas.getContext('2d'), { type, data, options });
  return canvas.toDataURL('image/jpeg', 1.0);
};

/* ─── helpers ──────────────────────────────────────────────── */
const levelLabel = (lvl) =>
  lvl === 1 ? 'Easy' : lvl === 2 ? 'Medium' : lvl === 3 ? 'Difficult' : '—';

/* ──────────────────────────────────────────────────────────── */
function AdminDashboard() {
  const navigate = useNavigate();

  /* loading bar */
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setProgress((p) => (p >= 90 ? p : p + 10)), 200);
    return () => clearInterval(t);
  }, [loading]);

  /* server data */
  const [questions,   setQuestions]   = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [qaList,      setQaList]      = useState([]);
  const [sessions,    setSessions]    = useState([]);
  const [pagination,  setPagination]  = useState({ total: 0, page: 1, limit: 20, pages: 0 });
  const [stats,       setStats]       = useState({ submitted: 0, approved: 0, rejected: 0, review: 0, draft: 0 });

  /* filters */
  const [teacherFilter, setTeacherFilter] = useState('All');
  const [qaFilter,      setQaFilter]      = useState('All');
  const [subjectFilter, setSubjectFilter] = useState('All');
  const [statusFilter,  setStatusFilter]  = useState('All');
  const [levelFilter,   setLevelFilter]   = useState('All');
  const [sessionFilter, setSessionFilter] = useState('All');

  const subjectList = [
    'All','Chinese 1','Chinese 2','Understanding of Holy Quran','Mathematics','Philosophy', 'Philosophy 1','English','Computer','Computer Science','IT','Physics',
    'Chemistry','Ethics','General Knowledge','General Science', 'Islamiat','Medical','Education','Business Administration','Commerce','Research/ORIC','Finance','Examinations','Civil','Electrical Engineering','Security','QEC'
  ];
  const statusList = ['All','draft','submitted','approved','rejected','review_needed'];

  /* UI feedback */
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  /* summary counts */
  const [submittedCount, setSubmitted] = useState(0);
  const [approvedCount,  setApproved]  = useState(0);
  const [rejectedCount,  setRejected]  = useState(0);
  const [reviewCount,    setReview]    = useState(0);

  /* modals */
  const [openView,     setOpenView]     = useState(false);
  const [openSubModal, setOpenSubModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const isSuperAdmin = currentRole === 'superadmin';

  /* CSV Permission management (superadmin only) */
  const [csvPermissionModalOpen, setCsvPermissionModalOpen] = useState(false);
  const [teachersWithCsvPermission, setTeachersWithCsvPermission] = useState([]);
  const [csvPermissionLoading, setCsvPermissionLoading] = useState(false);

  /* fetch on mount */
  useEffect(() => {
    (async () => {
      try {
        const [qRes, tRes, qaRes, sRes, statsRes] = await Promise.all([
          api.get('/api/dashboard/questions?page=1&limit=20'),
          api.get('/api/users/teacherList'),
          api.get('/api/users/qaList'),
          api.get('/api/sessions'),
          api.get('/api/dashboard/stats'),
        ]);
        setQuestions(qRes.data.questions || qRes.data);
        setPagination(qRes.data.pagination || { total: 0, page: 1, limit: 20, pages: 0 });
        setTeacherList(tRes.data);
        setQaList(qaRes.data);
        setSessions(sRes.data);
        
        // Set initial stats
        if (statsRes.data) {
          setStats({
            submitted: statsRes.data.submitted || 0,
            approved: statsRes.data.approved || 0,
            rejected: statsRes.data.rejected || 0,
            review: statsRes.data.review || 0,
            draft: statsRes.data.draft || 0
          });
        }
        
        setProgress(100);
        setTimeout(() => setLoading(false), 400);
      } catch {
        setError('Failed to load dashboard data.');
        setLoading(false);
      }
    })();
    // determine current role from token
    try {
      const r = getRoleFromToken();
      setCurrentRole(r);
    } catch (err) {
      setCurrentRole(null);
    }
  }, []);

  /* Build filter params for API calls */
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (sessionFilter && sessionFilter !== 'All') {
      params.append('sessionId', sessionFilter);
    }
    if (teacherFilter && teacherFilter !== 'All') {
      params.append('teacherId', teacherFilter);
    }
    if (qaFilter && qaFilter !== 'All') {
      params.append('qaId', qaFilter);
    }
    if (subjectFilter && subjectFilter !== 'All') {
      params.append('subject', subjectFilter);
    }
    if (statusFilter && statusFilter !== 'All') {
      params.append('status', statusFilter);
    }
    if (levelFilter && levelFilter !== 'All') {
      params.append('level', levelFilter);
    }
    return params;
  };

  /* fetch questions with pagination and all filters */
  const fetchQuestions = async (page = 1) => {
    try {
      setFilterLoading(true);
      const params = buildFilterParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      
      // Build stats params (same filters but without status filter for accurate counts)
      const statsParams = new URLSearchParams();
      if (sessionFilter && sessionFilter !== 'All') {
        statsParams.append('sessionId', sessionFilter);
      }
      if (teacherFilter && teacherFilter !== 'All') {
        statsParams.append('teacherId', teacherFilter);
      }
      if (qaFilter && qaFilter !== 'All') {
        statsParams.append('qaId', qaFilter);
      }
      if (subjectFilter && subjectFilter !== 'All') {
        statsParams.append('subject', subjectFilter);
      }
      if (levelFilter && levelFilter !== 'All') {
        statsParams.append('level', levelFilter);
      }
      
      const [qRes, statsRes] = await Promise.all([
        api.get(`/api/dashboard/questions?${params.toString()}`),
        api.get(`/api/dashboard/stats?${statsParams.toString()}`)
      ]);
      
      setQuestions(qRes.data.questions || qRes.data);
      setPagination(qRes.data.pagination || { total: 0, page, limit: 20, pages: 0 });
      
      // Set stats from backend (filtered)
      if (statsRes.data) {
        setStats({
          submitted: statsRes.data.submitted || 0,
          approved: statsRes.data.approved || 0,
          rejected: statsRes.data.rejected || 0,
          review: statsRes.data.review || 0,
          draft: statsRes.data.draft || 0
        });
      }
      
      setFilterLoading(false);
    } catch {
      setError('Failed to load questions.');
      setFilterLoading(false);
    }
  };

  /* CSV Permission management functions (superadmin only) */
  const fetchTeachersWithCsvPermission = async () => {
    try {
      setCsvPermissionLoading(true);
      const res = await api.get('/api/users/teachers-csv-permission');
      setTeachersWithCsvPermission(res.data);
    } catch (err) {
      setError('Failed to fetch teachers CSV permissions.');
    } finally {
      setCsvPermissionLoading(false);
    }
  };

  const handleToggleCsvPermission = async (teacherId, currentValue) => {
    try {
      await api.put(`/api/users/toggle-csv-permission/${teacherId}`, {
        canUploadCSV: !currentValue
      });
      // Update local state
      setTeachersWithCsvPermission(prev => 
        prev.map(t => t._id === teacherId ? { ...t, canUploadCSV: !currentValue } : t)
      );
      setSuccess(`CSV upload permission ${!currentValue ? 'enabled' : 'disabled'} successfully.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update CSV permission.');
    }
  };

  const handleOpenCsvPermissionModal = () => {
    fetchTeachersWithCsvPermission();
    setCsvPermissionModalOpen(true);
  };

  /* refetch when any filter changes */
  useEffect(() => {
    if (!loading) {
      fetchQuestions(1);
    }
  }, [sessionFilter, teacherFilter, qaFilter, subjectFilter, statusFilter, levelFilter]);


  /* rows - use questions directly from backend (already filtered) */
  const rows = questions;

  /* columns */
  const columns = [
    {
      field: 'questionSnippet',
      headerName: 'Question',
      flex: 3,
      minWidth: 250,
      valueGetter: ({ row }) => row?.question || '',          // guard #1
      renderCell: ({ row }) => {
        const fullText = (row?.question || '').replace(/<[^>]+>/g, '');
        const words = fullText.trim().split(/\s+/);
        const snippetText = isSuperAdmin 
          ? fullText.slice(0, 60) || '…'
          : (words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : ''));
        return (
          <Box>
            {row?.subQuestions?.length > 0 && (
              <Box sx={{ display:'inline-block', bgcolor:'cyan', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.7rem' }}>SubQ</Box>
            )}
            {row?.question?.includes('<img') && (
              <Box sx={{ display:'inline-block', bgcolor:'#ffcb00', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.7rem' }}>Img</Box>
            )}
            <span>{snippetText}</span>
          </Box>
        );
      },
    },
    { field: 'subject', headerName: 'Subject', width: 110 },
{
  field: 'level',
  headerName: 'Level',
  width: 90,
  renderCell: ({ row }) => levelLabel(row?.level),
},
    { field: 'teacherName', headerName: 'Teacher',     width: 110 },
    { field: 'qaName',      headerName: 'Assigned QA', width: 120 },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 120,
      valueGetter: ({ row }) => {
        const dateVal = row?.dateCreated;
        if (!dateVal) return null;
        return dateVal;
      },
      renderCell: ({ row }) => {
        const dateVal = row?.dateCreated;
        if (!dateVal) return '—';
        try {
          const date = new Date(dateVal);
          if (isNaN(date.getTime())) return '—';
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric' 
          });
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => {
        const palette = { draft:'#888', submitted:'orange', approved:'green', rejected:'red', review_needed:'blue' };
        const label = value === 'review_needed' ? 'Review Needed' : value.charAt(0).toUpperCase()+value.slice(1);
        return (
          <Box sx={{ bgcolor:palette[value], color:'#fff', borderRadius:1, px:1, py:0.25, width:'100%', textAlign:'center' }}>
            {label}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" variant="outlined" startIcon={<EyeIcon />} onClick={() => handleView(row)}>
          View
        </Button>
      ),
    },
  ];

  /* ───────── PDF report ───────── */
  const buildPdf = async () => {
    try {
      setError('');
      setFilterLoading(true);
      
      const pdfParams = buildFilterParams();
      pdfParams.append('page', '1');
      pdfParams.append('limit', '999999');
      
      const response = await api.get(`/api/dashboard/questions?${pdfParams.toString()}`);
      const allQuestions = response.data.questions || [];
      
      setFilterLoading(false);
      
      if (allQuestions.length === 0) {
        setError('No questions available to generate PDF report.');
        return;
      }

      const doc = new jsPDF({ orientation: 'landscape' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Calculate statistics
      const statusData = {
        draft: allQuestions.filter(q => q.status === 'draft').length,
        submitted: allQuestions.filter(q => q.status === 'submitted').length,
        approved: allQuestions.filter(q => q.status === 'approved').length,
        rejected: allQuestions.filter(q => q.status === 'rejected').length,
        review_needed: allQuestions.filter(q => q.status === 'review_needed').length
      };
      const levelData = {
        easy: allQuestions.filter(q => q.level === 1).length,
        medium: allQuestions.filter(q => q.level === 2).length,
        hard: allQuestions.filter(q => q.level === 3).length
      };
      const total = allQuestions.length;
      const approvalRate = total > 0 ? ((statusData.approved / total) * 100).toFixed(1) : '0.0';

      // Aggregate teacher data
      const teacherData = {};
      allQuestions.forEach(q => {
        const name = q.teacherName || 'Unknown';
        if (!teacherData[name]) {
          teacherData[name] = { draft: 0, submitted: 0, approved: 0, rejected: 0, review_needed: 0, L1: 0, L2: 0, L3: 0, total: 0 };
        }
        teacherData[name][q.status] = (teacherData[name][q.status] || 0) + 1;
        teacherData[name]['L' + q.level] = (teacherData[name]['L' + q.level] || 0) + 1;
        teacherData[name].total += 1;
      });

      const totalPages = Object.keys(teacherData).length + 1;

      // Build filter text
      const filters = [];
      if (sessionFilter !== 'All') filters.push('Session: ' + (sessions.find(s => s._id === sessionFilter)?.name || sessionFilter));
      if (teacherFilter !== 'All') filters.push('Teacher: ' + (teacherList.find(t => t._id === teacherFilter)?.username || teacherFilter));
      if (qaFilter !== 'All') filters.push('QA: ' + (qaList.find(q => q._id === qaFilter)?.username || qaFilter));
      if (subjectFilter !== 'All') filters.push('Subject: ' + subjectFilter);
      if (statusFilter !== 'All') filters.push('Status: ' + statusFilter);
      if (levelFilter !== 'All') filters.push('Level: ' + levelFilter);

      /* ══════════════════════════════════════════════════════════════════
         PAGE 1: SUMMARY
         ══════════════════════════════════════════════════════════════════ */
      
      // Header
      doc.setFillColor(44, 62, 80);
      doc.rect(0, 0, pageW, 22, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('AROR UNIVERSITY CBT PORTAL', 15, 14);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageW - 15, 10, { align: 'right' });
      doc.setFontSize(9);
      doc.setTextColor(255, 200, 200);
      doc.text('CONFIDENTIAL', pageW - 15, 18, { align: 'right' });

      let y = 30;

      // Filters (if any)
      if (filters.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Filters: ' + filters.join(' | '), 15, y);
        y += 8;
      }

      // Summary Cards
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Overview', 15, y);
      y += 6;

      const cardData = [
        { label: 'Total', value: total, color: [52, 152, 219] },
        { label: 'Approved', value: statusData.approved, color: [46, 204, 113] },
        { label: 'Submitted', value: statusData.submitted, color: [241, 196, 15] },
        { label: 'Rejected', value: statusData.rejected, color: [231, 76, 60] },
        { label: 'Review', value: statusData.review_needed, color: [52, 73, 94] },
        { label: 'Rate', value: approvalRate + '%', color: [155, 89, 182] }
      ];

      const cardW = 42;
      const cardH = 22;
      const cardGap = 4;
      cardData.forEach((card, i) => {
        const x = 15 + i * (cardW + cardGap);
        doc.setFillColor(...card.color);
        doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(String(card.value), x + cardW / 2, y + 10, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(card.label, x + cardW / 2, y + 17, { align: 'center' });
      });

      y += cardH + 12;

      // Status Table
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Status Distribution', 15, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        margin: { left: 15 },
        tableWidth: 120,
        head: [['Status', 'Count', 'Percentage']],
        body: [
          ['Draft', statusData.draft, total ? (statusData.draft / total * 100).toFixed(1) + '%' : '0%'],
          ['Submitted', statusData.submitted, total ? (statusData.submitted / total * 100).toFixed(1) + '%' : '0%'],
          ['Approved', statusData.approved, total ? (statusData.approved / total * 100).toFixed(1) + '%' : '0%'],
          ['Rejected', statusData.rejected, total ? (statusData.rejected / total * 100).toFixed(1) + '%' : '0%'],
          ['Review Needed', statusData.review_needed, total ? (statusData.review_needed / total * 100).toFixed(1) + '%' : '0%'],
        ],
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [44, 62, 80], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } }
      });

      // Level Table (right side)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Difficulty Distribution', 150, y);

      autoTable(doc, {
        startY: y + 4,
        margin: { left: 150 },
        tableWidth: 100,
        head: [['Level', 'Count', 'Percentage']],
        body: [
          ['Easy', levelData.easy, total ? (levelData.easy / total * 100).toFixed(1) + '%' : '0%'],
          ['Medium', levelData.medium, total ? (levelData.medium / total * 100).toFixed(1) + '%' : '0%'],
          ['Difficult', levelData.hard, total ? (levelData.hard / total * 100).toFixed(1) + '%' : '0%'],
        ],
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [46, 204, 113], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 250, 247] },
        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } }
      });

      y += 55;

      // Teacher Summary Table
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text('Teacher Summary', 15, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        margin: { left: 15, right: 15 },
        head: [['Teacher', 'Draft', 'Submitted', 'Approved', 'Rejected', 'Review', 'Easy', 'Medium', 'Hard', 'Total']],
        body: Object.entries(teacherData).map(([name, d]) => [
          name, d.draft, d.submitted, d.approved, d.rejected, d.review_needed, d.L1, d.L2, d.L3, d.total
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [44, 62, 80], textColor: 255, fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        columnStyles: {
          0: { cellWidth: 50 },
          3: { textColor: [46, 204, 113] },
          4: { textColor: [231, 76, 60] },
          9: { fontStyle: 'bold' }
        }
      });

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Page 1 of ' + totalPages, pageW / 2, pageH - 8, { align: 'center' });

      /* ══════════════════════════════════════════════════════════════════
         PAGES 2+: TEACHER DETAILS
         ══════════════════════════════════════════════════════════════════ */
      
      let pageNum = 2;
      Object.entries(teacherData).forEach(([teacher, data]) => {
        doc.addPage('landscape');

        // Header
        doc.setFillColor(44, 62, 80);
        doc.rect(0, 0, pageW, 22, 'F');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('AROR UNIVERSITY CBT PORTAL - Teacher Report', 15, 14);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(255, 200, 200);
        doc.text('CONFIDENTIAL', pageW - 15, 14, { align: 'right' });

        // Teacher Info Bar
        const rate = data.total > 0 ? ((data.approved / data.total) * 100).toFixed(1) : '0.0';
        doc.setFillColor(248, 249, 250);
        doc.setDrawColor(200, 200, 200);
        doc.roundedRect(15, 26, pageW - 30, 12, 2, 2, 'FD');
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text(teacher, 20, 34);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Total: ' + data.total + '  |  Approved: ' + data.approved + '  |  Approval Rate: ' + rate + '%', pageW - 20, 34, { align: 'right' });

        // Status Table
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('Question Status', 15, 48);

        autoTable(doc, {
          startY: 52,
          margin: { left: 15 },
          tableWidth: 130,
          head: [['Status', 'Count', 'Percentage']],
          body: [
            ['Draft', data.draft, data.total ? (data.draft / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Submitted', data.submitted, data.total ? (data.submitted / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Approved', data.approved, data.total ? (data.approved / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Rejected', data.rejected, data.total ? (data.rejected / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Review Needed', data.review_needed, data.total ? (data.review_needed / data.total * 100).toFixed(1) + '%' : '0%'],
          ],
          styles: { fontSize: 10, cellPadding: 4 },
          headStyles: { fillColor: [52, 152, 219], textColor: 255 },
          alternateRowStyles: { fillColor: [245, 248, 255] },
          columnStyles: { 
            0: { cellWidth: 50 },
            1: { halign: 'center', cellWidth: 40 }, 
            2: { halign: 'center', cellWidth: 40 } 
          }
        });

        // Level Table
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('Difficulty Level', 160, 48);

        autoTable(doc, {
          startY: 52,
          margin: { left: 160 },
          tableWidth: 110,
          head: [['Level', 'Count', 'Percentage']],
          body: [
            ['Easy', data.L1, data.total ? (data.L1 / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Medium', data.L2, data.total ? (data.L2 / data.total * 100).toFixed(1) + '%' : '0%'],
            ['Difficult', data.L3, data.total ? (data.L3 / data.total * 100).toFixed(1) + '%' : '0%'],
          ],
          styles: { fontSize: 10, cellPadding: 4 },
          headStyles: { fillColor: [46, 204, 113], textColor: 255 },
          alternateRowStyles: { fillColor: [245, 250, 247] },
          columnStyles: { 
            0: { cellWidth: 40 },
            1: { halign: 'center', cellWidth: 35 }, 
            2: { halign: 'center', cellWidth: 35 } 
          }
        });

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text('Page ' + pageNum + ' of ' + totalPages, pageW / 2, pageH - 8, { align: 'center' });
        
        pageNum++;
      });

      doc.save('CBT_Question_Report.pdf');
    } catch (err) {
      setFilterLoading(false);
      setError('Failed to generate PDF report. Please try again.');
      console.error('PDF generation error:', err);
    }
  };

  /* ───────── helpers ───────── */
  const handleView = (row) => {
    setSelectedQuestion(row);
    setOpenView(true);
  };
  const closeView = () => {
    setOpenView(false);
    setOpenSubModal(false);
    setSelectedQuestion(null);
  };
  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };



  /* ───────── Loading screen ───────── */
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-100px',
            right: '-100px',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            bottom: '-50px',
            left: '-50px',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 4,
              p: 5,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
            }}
          >
            {/* Animated Logo/Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.5px',
                }}
              >
                CBT
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                mb: 1,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Loading Dashboard
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#7f8c8d',
                mb: 4,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Preparing your workspace...
            </Typography>

            {/* Modern Progress Bar */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e7ff',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>
            
            <Typography
              variant="body2"
              sx={{
                color: '#667eea',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {progress}%
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  /* ───────── MAIN UI ───────── */
  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: { xs: 3, md: 4 },
          pb: 3,
          borderBottom: '3px solid',
          borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5, fontSize: { xs: '1.75rem', sm: '2rem', md: '3rem' } }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Monitor and manage all questions across sessions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={buildPdf}
            fullWidth={true}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Generate&nbsp;Report
          </Button>
          {isSuperAdmin && (
            <Button
              variant="contained"
              onClick={() => navigate('/reset-password')}
              fullWidth={true}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  background: 'linear-gradient(135deg, #df7eea 0%, #e4465b 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(240, 147, 251, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              User&nbsp;Management
            </Button>
          )}
          {isSuperAdmin && (
            <Button 
              variant="contained" 
              onClick={handleOpenCsvPermissionModal}
              startIcon={<CloudUploadIcon />}
              fullWidth={true}
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              CSV&nbsp;Permissions
            </Button>
          )}
          <Button 
            variant="outlined" 
            onClick={logout}
            fullWidth={true}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              borderWidth: 2,
              borderColor: '#dc3545',
              color: '#dc3545',
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': {
                borderWidth: 2,
                borderColor: '#c82333',
                background: '#fff5f5',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            label: 'Submitted',
            icon: <HourglassEmpty sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            value: stats.submitted,
          },
          {
            label: 'Approved',
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            value: stats.approved,
          },
          {
            label: 'Rejected',
            icon: <Cancel sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            value: stats.rejected,
          },
          {
            label: 'Review Needed',
            icon: <ReportProblem sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            value: stats.review,
          },
        ].map((c, i) => (
          <Grid key={i} item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: c.gradient,
                color: '#fff',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}>
                      {c.label}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {c.value}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {c.icon}
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total count
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* teacher */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Teacher</InputLabel>
          <Select
            label="Teacher"
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {teacherList.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* QA */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>QA</InputLabel>
          <Select
            label="QA"
            value={qaFilter}
            onChange={(e) => setQaFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {qaList.map((qa) => (
              <MenuItem key={qa._id} value={qa._id}>
                {qa.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* subject */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            label="Subject"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            {subjectList.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* status */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusList.map((s) => (
              <MenuItem key={s} value={s}>
                {s === 'review_needed'
                  ? 'Review Needed'
                  : s === 'All'
                  ? 'All'
                  : s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* level */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value={1}>Easy (1)</MenuItem>
            <MenuItem value={2}>Medium (2)</MenuItem>
            <MenuItem value={3}>Difficult (3)</MenuItem>
          </Select>
        </FormControl>

            {/* session */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Session</InputLabel>
          <Select
            label="Session"
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {sessions.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Questions List ({pagination.total} total)
          </Typography>
        </Box>
        <Box sx={{ height: 550, width: '100%', position: 'relative' }}>
          {filterLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          )}
          <DataGrid
            rows={rows.map((q) => ({ ...q, questionSnippet: q.question }))}
            columns={columns}
            getRowId={(r) => r._id}
            paginationModel={{ page: pagination.page - 1, pageSize: 20 }}
            onPaginationModelChange={(model) => {
              if (model.page !== pagination.page - 1) {
                fetchQuestions(model.page + 1);
              }
            }}
            pageSizeOptions={[20]}
            slots={{ toolbar: GridToolbar }}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={pagination.total}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: '#f8f9ff',
              },
            }}
          />
        </Box>
      </Card>

      {/* Question Modal */}
      <Dialog 
        open={openView} 
        onClose={closeView} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Question Details
        </DialogTitle>
        {selectedQuestion && (
          <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
            {/* Info Cards Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #667eea',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Subject
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                    {selectedQuestion.subject}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #11998e',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Difficulty Level
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                    {levelLabel(selectedQuestion.level)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #f093fb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Status
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: selectedQuestion.status === 'approved' ? '#4caf50' : 
                           selectedQuestion.status === 'rejected' ? '#f44336' : '#ff9800',
                    fontWeight: 700,
                    textTransform: 'capitalize'
                  }}>
                    {selectedQuestion.status === 'review_needed' ? 'Review Needed' : selectedQuestion.status}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #fa709a',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Teacher
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                    {selectedQuestion.teacherName || selectedQuestion.teacherId}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {selectedQuestion.qaName && (
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 2,
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                  Assigned QA
                </Typography>
                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                  {selectedQuestion.qaName}
                </Typography>
              </Box>
            )}

            {selectedQuestion.reviewComment && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Review Comment:
                </Typography>
                <Typography variant="body2">
                  {selectedQuestion.reviewComment}
                </Typography>
              </Alert>
            )}

            {/* Question Text */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Question
              </Typography>
              {isSuperAdmin ? (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                    '& p': { marginBottom: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedQuestion.question }}
                />
              ) : (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2, 
                  border: '1px solid #e0e0e0',
                }}
                >
                  {(() => {
                    const fullText = (selectedQuestion.question || '').replace(/<[^>]+>/g, '');
                    const words = fullText.trim().split(/\s+/);
                    return words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : '');
                  })()}
                </Box>
              )}
            </Box>

            {/* Options (hidden for admin-level users) */}
            {isSuperAdmin ? (
              selectedQuestion.options?.length > 0 && (
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 3, 
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                    Answer Options
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedQuestion.options.map((opt, i) => {
                      const isCorrect = opt === selectedQuestion.correctAnswer;
                      return (
                        <Grid item xs={12} sm={6} key={i}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                              bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Box sx={{ 
                              minWidth: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}>
                              {String.fromCharCode(65 + i)}
                            </Box>
                            <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                              {isCorrect && (
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  color: '#4caf50',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                  ✓ Correct Answer
                                </Typography>
                              )}
                              <Box dangerouslySetInnerHTML={{ __html: opt }} />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )
            ) : (
              <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, mb: 2 }}>
                <Typography sx={{ color: '#64748b' }}>Answer options are hidden for Admin role.</Typography>
              </Box>
            )}

            {/* Sub Questions Button */}
            {selectedQuestion.subQuestions?.length > 0 && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setOpenSubModal(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View {selectedQuestion.subQuestions.length} Sub Question{selectedQuestion.subQuestions.length > 1 ? 's' : ''}
              </Button>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={closeView}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-questions Modal */}
      <Dialog
        open={openSubModal}
        onClose={() => setOpenSubModal(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Sub Questions ({selectedQuestion?.subQuestions?.length || 0})
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          {selectedQuestion?.subQuestions?.map((sq, i) => (
            <Box 
              key={i} 
              sx={{ 
                bgcolor: 'white',
                borderRadius: 2,
                p: 3,
                mb: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #11998e',
              }}
            >
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: '#2c3e50',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  bgcolor: '#11998e',
                  color: 'white',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                }}>
                  {i + 1}
                </Box>
                Sub Question {i + 1}
              </Typography>
              
              <Box
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: sq.question }}
              />

              {sq.options?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1.5 }}>
                    Answer Options
                  </Typography>
                  <Grid container spacing={2}>
                    {sq.options.map((opt, j) => {
                      const isCorrect = opt === sq.correctAnswer;
                      return (
                        <Grid item xs={12} sm={6} key={j}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                              bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ 
                              minWidth: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              flexShrink: 0,
                            }}>
                              {String.fromCharCode(65 + j)}
                            </Box>
                            <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                              {isCorrect && (
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  color: '#4caf50',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                  ✓ Correct Answer
                                </Typography>
                              )}
                              <Box dangerouslySetInnerHTML={{ __html: opt }} />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={() => setOpenSubModal(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── CSV Permission Management Modal (Superadmin only) ─────────────── */}
      <Dialog 
        open={csvPermissionModalOpen} 
        onClose={() => setCsvPermissionModalOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudUploadIcon />
            CSV Upload Permissions
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Control which teachers can upload questions via CSV files. Only teachers with this permission enabled will see the "Upload CSV" button in their Question Bank.
            </Typography>
          </Alert>

          {csvPermissionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>CSV Upload</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teachersWithCsvPermission.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: '#7f8c8d' }}>
                        No teachers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    teachersWithCsvPermission.map((teacher) => (
                      <TableRow 
                        key={teacher._id}
                        sx={{ 
                          '&:hover': { bgcolor: '#f8f9fa' },
                          opacity: teacher.disabled ? 0.5 : 1,
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {teacher.username}
                            </Typography>
                            {teacher.disabled && (
                              <Chip 
                                label="Disabled" 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {teacher.canUploadCSV ? (
                            <Chip 
                              label="Enabled" 
                              size="small" 
                              color="success" 
                              icon={<CheckCircle />}
                            />
                          ) : (
                            <Chip 
                              label="Disabled" 
                              size="small" 
                              color="default" 
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Tooltip title={teacher.canUploadCSV ? 'Disable CSV upload' : 'Enable CSV upload'}>
                            <Switch
                              checked={teacher.canUploadCSV || false}
                              onChange={() => handleToggleCsvPermission(teacher._id, teacher.canUploadCSV)}
                              disabled={teacher.disabled}
                              color="success"
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa', borderTop: '2px solid #e0e0e0' }}>
          <Button 
            onClick={() => setCsvPermissionModalOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard;
